import React, { useState, useEffect } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import { Row, Space, Spinner, Button } from "@masterteam/mt-design-react";

import Table from "../../NewTable";
import WorkflowModal from "../WorkflowModal";

import api from "../../../providers/api";
import useIsAr from "../../../Hooks/useIsAr";
import { getTasks } from "../../../reducers/taskCenter";
import { addToast, getLookupName } from "../../../helpers";

import { ReactComponent as InfoIcon } from "../../../assets/images/info-icon.svg";
import { ReactComponent as HistoryIcon } from "../../../assets/images/history.svg";
import { ReactComponent as ActionsIcon } from "../../../assets/images/actions.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

const MyTasks = () => {
  const intl = useIntl();
  const [isAr] = useIsAr();
  const history = useHistory();
  const dispatch = useDispatch();
  const [workflowModal, setWorkflowModal] = useState(initialModalState);

  const [TaskCenterActions, BaseWorkflowTypes] = useSelector(({ theme }) => [
    theme.TaskCenterActions.item.Items.filter((e) => e.Value !== "All"),
    theme.BaseWorkflowTypes.item.Items,
  ]);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(true);

  const handleOnClick = (item) => {
    const body = {
      Level: item?.Level,
      LevelID: item?.LevelID,
      BaseWorkflowID: item?.BaseWorkflowID,
    };
    markAsRead(body);
    if (item.Level === 1) history.push(`/splus/kpi-details/${item?.LevelID}`);
  };

  const markAsRead = async (body) => {
    try {
      const res = await api.taskCenter.readTask(body);

      if (res.StatusCode === "Success" && res.Data) dispatch(getTasks());
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    }
  };

  const getData = async (newKey) => {
    try {
      setLoading(!newKey);
      setContentLoading(true);

      const res = await api.taskCenter.getMyTasks();

      setData(res?.Data || []);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
      setContentLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  const columns = [
    {
      key: "assignment-type",
      header: <FormattedMessage id="my-task.assignment-type" />,
      cell: (item) => {
        return (
          <div style={{ fontWeight: item?.IsRead ? "" : "bold" }}>
            {item?.Action === 2
              ? getLookupName(TaskCenterActions, item?.Action.toString(), isAr)
              : getLookupName(
                  BaseWorkflowTypes,
                  item?.BaseWorkflowID.toString(),
                  isAr
                )}
          </div>
        );
      },
      width: "15%",
    },
    {
      key: "workFlow-type",
      header: <FormattedMessage id="my-task.workflow-type" />,
      cell: (item) => {
        return (
          <div style={{ fontWeight: item?.IsRead ? "" : "bold" }}>
            {item?.Action === 1
              ? "---"
              : getLookupName(
                  BaseWorkflowTypes,
                  item?.BaseWorkflowID.toString(),
                  isAr
                )}
          </div>
        );
      },
      width: "15%",
    },
    {
      key: "name",
      header: <FormattedMessage id="my-task.name" />,
      cell: (item) => (isAr ? item?.ArabicName : item?.EnglishName),
      width: "25%",
    },
    {
      key: "received-date",
      header: <FormattedMessage id="my-task.received-date" />,
      cell: (item) =>
        item.Created ? moment(item.Created).format("DD-MM-YYYY") : "---",
      width: "15%",
    },
    {
      key: "due-date",
      header: (
        <Space align="center" gap={0}>
          <FormattedMessage id="my-task.due-date" />

          <Button
            shape="round"
            Icon={InfoIcon}
            tooltip-position="top"
            tooltip={intl.formatMessage({ id: "my-task.due-date-tooltip" })}
          />
        </Space>
      ),
      cell: (item) =>
        item.DueDate ? moment(item.DueDate).format("DD-MM-YYYY") : "---",
      width: "15%",
    },
    {
      key: "workflow",
      header: <FormattedMessage id="my-task.workflow" />,
      cell: (item) => (
        <div className="icon-wrapper">
          <HistoryIcon
            onClick={(e) => {
              e.stopPropagation();
              setWorkflowModal({ isOpen: true, data: item.Request });
            }}
          />
        </div>
      ),
      width: "15%",
    },
    {
      key: "actions",
      header: <FormattedMessage id="my-task.actions" />,
      cell: (item) => (
        <div className="icon-wrapper">
          <ActionsIcon
            onClick={(e) => {
              e.stopPropagation();
              handleOnClick(item);
            }}
          />
        </div>
      ),
      width: "15%",
    },
  ];

  return loading ? (
    <Row justify="center" style={{ marginTop: "24px" }}>
      <Spinner size="large" />
    </Row>
  ) : (
    <div>
      {contentLoading ? (
        <Row justify="center" style={{ marginTop: "24px" }}>
          <Spinner size="large" />
        </Row>
      ) : (
        <Table
          compact
          data={data || []}
          columns={columns}
          differentRowsColor
          headerHasBackgroundColor
          onClickItem={handleOnClick}
        />
      )}

      {workflowModal.isOpen && (
        <WorkflowModal
          data={workflowModal.data}
          onClose={() => setWorkflowModal(initialModalState)}
        />
      )}
    </div>
  );
};

export default MyTasks;
